import { FunctionComponent, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { ActAsPicker } from "components/organisms/act-as-picker";
import { useActAs } from "contexts/ActAsContext";
import { parseActAsExpiry } from "core/act-as/act-as.session";
import { formatActAsSubject } from "core/api/act-as/act-as.helpers";
import { isOperatorRole } from "core/constants/roles";
import { authSelectors } from "core/store/modules/auth";
import { Dispatch, RootState } from "core/store/store.types";
import { User } from "core/types/user.types";

interface OwnProps {}

interface StateProps {
  user?: User;
}

interface DispatchProps {}

export type Props = OwnProps & StateProps & DispatchProps;

const formatRemaining = (expiresAt: string, now: number): string => {
  const expiry = parseActAsExpiry(expiresAt);
  if (Number.isNaN(expiry)) return "";
  const remaining = Math.max(expiry - now, 0);
  const totalMinutes = Math.floor(remaining / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  const seconds = Math.floor((remaining % 60000) / 1000);
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
};

const ActAsBar: FunctionComponent<Props> = ({ user }) => {
  const [t] = useTranslation();
  const { session, stop, isStopping } = useActAs();
  const [openPicker, setOpenPicker] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!session) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [session]);

  // Il banner viene PRIMA del controllo sul ruolo: finche' esiste una sessione, deve
  // vedersi comunque. Una delega attiva e invisibile e' il guasto peggiore di questa
  // feature, peggio di un banner di troppo.
  if (session) {
    const remaining = formatRemaining(session.expiresAt, now);
    return (
      // Banner permanente e non chiudibile: finche' e' a schermo, tutto quello che
      // l'operatore crea appartiene al cliente indicato.
      <Alert
        severity="info"
        variant="filled"
        icon={<SupervisorAccountIcon />}
        sx={{
          borderRadius: 0,
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <span>
            <strong>{t("act-as.banner-title")}</strong> —{" "}
            {t("act-as.banner-message", {
              customer: formatActAsSubject(session.subject),
            })}
            {remaining
              ? ` (${t("act-as.expires-in", { time: remaining })})`
              : ""}
          </span>
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            disabled={isStopping}
            onClick={() => stop()}
            startIcon={isStopping ? <CircularProgress size={14} /> : undefined}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {t("act-as.stop")}
          </Button>
        </Box>
      </Alert>
    );
  }

  // Il comando per aprire una delega si mostra solo agli operatori. Non e' una misura di
  // sicurezza: la difesa vera e' server-side (l'header mandato da un 'user' e' un 403).
  if (!isOperatorRole(user?.role)) return null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 4,
          py: 2,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<SupervisorAccountIcon />}
          onClick={() => setOpenPicker(true)}
        >
          {t("act-as.start-cta")}
        </Button>
      </Box>
      <ActAsPicker isVisible={openPicker} setIsVisible={setOpenPicker} />
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    user: authSelectors.getUser(state),
  }),
  (dispatch: Dispatch) => ({})
)(ActAsBar);
